import {AttractionItemType} from "@/entities/destination/attraction/types";

/**
 * Helper functions for attraction routes
 * Handles validation and conversion of attraction type parameters
 */
export const attractionHelpers = {
    /**
     * Normalize attraction param (convert hyphens to spaces)
     * @example normalizeParam("things-to-do") => "things to do"
     */
    normalizeParam(param: string): string {
        return param.replace(/-/g, " ").toLowerCase().trim();
    },

    /**
     * Check if attraction type is valid
     * @example isValidAttractionType("things-to-do") => true
     * @example isValidAttractionType("invalid-type") => false
     */
    isValidAttractionType(param: string): boolean {
        const normalized = this.normalizeParam(param);
        return Object.values(AttractionItemType).includes(normalized as AttractionItemType);
    },

    /**
     * Get enum value from URL param
     * @throws Error if param is invalid
     * @example getEnumValue("things-to-do") => "things to do"
     * @example getEnumValue("invalid") => throws Error
     */
    getEnumValue(param: string): AttractionItemType {
        const normalized = this.normalizeParam(param);
        const enumValue = Object.values(AttractionItemType).find((value) => value === normalized);

        if (!enumValue) {
            throw new Error(`Invalid attraction type: "${param}"`);
        }

        return enumValue as AttractionItemType;
    },

    /**
     * Format enum value to URL param (spaces to hyphens)
     * @example formatToUrl("things to do") => "things-to-do"
     */
    formatToUrl(enumValue: AttractionItemType): string {
        return enumValue.replace(/\s+/g, "-");
    },

    /**
     * Validate that URL attraction type matches database attraction type
     * @param urlParam - Attraction type from URL (e.g., "things-to-do")
     * @param dbType - Attraction type from database (e.g., "things to do")
     * @returns true if types match, false otherwise
     * @example matchesAttractionType("things-to-do", "things to do") => true
     * @example matchesAttractionType("restaurants", "things to do") => false
     */
    matchesAttractionType(urlParam: string, dbType: string): boolean {
        try {
            const urlEnumValue = this.getEnumValue(urlParam);
            const normalizedDbType = dbType.toLowerCase().trim();
            return urlEnumValue === normalizedDbType;
        } catch {
            return false;
        }
    },
} as const;

/**
 * Type helper for attraction helpers
 */
export type AttractionHelpers = typeof attractionHelpers;
